/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * Control Panel → "Active Runs" tab. Lists in-flight (and, filterable, terminal)
 * scaffolder tasks; lets an operator graceful-cancel / retry (proxied to the
 * scaffolder API) and force-close an orphaned task (break-glass, ops-gated).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import CancelIcon from '@material-ui/icons/Cancel';
import ReplayIcon from '@material-ui/icons/Replay';
import WarningIcon from '@material-ui/icons/Warning';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import {
  InfoCard,
  Progress,
  StatusOK,
  StatusError,
  StatusPending,
  StatusAborted,
  StatusRunning,
  WarningPanel,
} from '@backstage/core-components';
import { HowItWorks, HowItWorksStep } from '../common/HowItWorks';
import {
  ActiveRun,
  RunStatus,
  TaskEvent,
  useActiveRunsWatcherAccess,
  useActiveRunsWatcherApi,
} from './useActiveRunsWatcherApi';

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  search: { minWidth: 260 },
  // Fixed-width status filter so the control doesn't grow/shrink with the
  // selection (the "renders weird" fix); the menu is width-matched below.
  statusFilter: { width: 220 },
  grow: { flex: 1 },
  meta: { color: theme.palette.text.secondary, fontSize: '0.8rem' },
  idCell: { fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' },
  orphanChip: { marginLeft: theme.spacing(1) },
  actions: { display: 'flex', gap: theme.spacing(0.5), justifyContent: 'flex-end' },
  drawerPaper: { width: 560, maxWidth: '90vw', padding: theme.spacing(3) },
  eventRow: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    title: 'Watch',
    body: (
      <>
        Defaults to in-flight runs (open + processing); use the status filter for
        terminal ones or “Orphaned only” to isolate stuck tasks. The ⓘ opens the
        task’s event log.
      </>
    ),
  },
  {
    title: 'Cancel / Retry',
    body: (
      <>
        Cancel gracefully stops a <b>live</b> run (it signals the running
        worker); Retry re-runs a failed one. Both go through the scaffolder’s own
        API — the normal, safe path.
      </>
    ),
  },
  {
    title: 'Force-close',
    body: (
      <>
        For an <b>orphaned</b> task only. Cancel can’t reach it (no worker to
        signal), so force-close marks it <code>cancelled</code> directly. It
        refuses any task that isn’t a confirmed orphan, so a live run is never
        affected.
      </>
    ),
  },
];

const STATUS_OPTIONS: RunStatus[] = [
  'open',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'skipped',
];

const StatusMark = ({ status, orphaned }: { status: RunStatus; orphaned: boolean }) => {
  if (orphaned) return <StatusAborted>orphaned</StatusAborted>;
  switch (status) {
    case 'processing':
      return <StatusRunning>processing</StatusRunning>;
    case 'open':
      return <StatusPending>open</StatusPending>;
    case 'completed':
      return <StatusOK>completed</StatusOK>;
    case 'failed':
      return <StatusError>failed</StatusError>;
    case 'cancelled':
      return <StatusAborted>cancelled</StatusAborted>;
    default:
      return <StatusPending>{status}</StatusPending>;
  }
};

const relTime = (iso: string | null): string => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)}m ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const shortId = (id: string) => id.slice(0, 8);
// In-flight statuses lead the default view; then most-recently-created first.
const IN_FLIGHT = new Set(['open', 'processing']);

export const ActiveRunsWatcher = () => {
  const classes = useStyles();
  const api = useActiveRunsWatcherApi();
  const { canWrite } = useActiveRunsWatcherAccess();

  const [runs, setRuns] = useState<(ActiveRun & { busy?: boolean })[] | undefined>();
  const [meta, setMeta] = useState<{
    orphanAfterSeconds: number;
    forceCloseEnabled: boolean;
    builtAt: number;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [orphanOnly, setOrphanOnly] = useState(false);
  const [toast, setToast] = useState<string>();
  const [confirmForce, setConfirmForce] = useState<ActiveRun | undefined>();
  const [detail, setDetail] = useState<ActiveRun | undefined>();
  const [detailEvents, setDetailEvents] = useState<TaskEvent[] | undefined>();
  const [detailError, setDetailError] = useState<string>();

  // Guards against out-of-order list responses: switching the status filter
  // fires a new fetch, and a slow earlier one must not overwrite a newer result
  // (same hazard openDetail guards). Also drops a resolve after unmount.
  const loadReqId = useRef(0);
  const load = useCallback(
    async (statuses: string[]) => {
      const reqId = ++loadReqId.current;
      setLoading(true);
      setError(undefined);
      try {
        const list = await api.listRuns(statuses);
        if (loadReqId.current !== reqId) return; // superseded by a newer load
        setRuns(list.runs);
        setMeta({
          orphanAfterSeconds: list.orphanAfterSeconds,
          forceCloseEnabled: list.forceCloseEnabled,
          builtAt: list.builtAt,
        });
      } catch (e) {
        if (loadReqId.current !== reqId) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (loadReqId.current === reqId) setLoading(false);
      }
    },
    [api],
  );

  // Re-fetch when the status filter changes (server-side filtering keeps the
  // fetch bounded); empty -> backend's in-flight default.
  useEffect(() => {
    load(statusFilter);
  }, [load, statusFilter]);

  const patch = useCallback((id: string, p: Partial<ActiveRun & { busy: boolean }>) => {
    setRuns(prev => prev?.map(r => (r.id === id ? { ...r, ...p } : r)));
  }, []);

  const runAction = useCallback(
    async (run: ActiveRun, kind: 'cancel' | 'retry' | 'force-close') => {
      patch(run.id, { busy: true });
      try {
        if (kind === 'cancel') await api.cancel(run.id);
        else if (kind === 'retry') await api.retry(run.id);
        else await api.forceClose(run.id);
        setToast(`${shortId(run.id)}: ${kind} ok`);
        await load(statusFilter);
      } catch (e) {
        setToast(`${shortId(run.id)}: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        // Always clear busy — a successful load() replaces the row anyway; this
        // guards the row from a stuck spinner if the post-action reload failed.
        patch(run.id, { busy: false });
      }
    },
    [api, patch, load, statusFilter],
  );

  // Guards the drawer against a stale events response: opening row B while row
  // A's fetch is still in flight must not let A's (late) result overwrite B's.
  const detailReqId = useRef<string>();
  const openDetail = useCallback(
    async (run: ActiveRun) => {
      setDetail(run);
      setDetailEvents(undefined);
      setDetailError(undefined);
      detailReqId.current = run.id;
      try {
        const res = await api.events(run.id);
        if (detailReqId.current !== run.id) return; // superseded by a newer open
        const evs = Array.isArray(res) ? res : res.events ?? [];
        setDetailEvents(evs);
      } catch (e) {
        if (detailReqId.current !== run.id) return;
        setDetailError(e instanceof Error ? e.message : String(e));
      }
    },
    [api],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (runs ?? []).filter(r => {
      if (orphanOnly && !r.orphaned) return false;
      if (q) {
        const hay = `${r.id} ${r.templateRef ?? ''} ${r.createdBy ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Orphaned first, then in-flight, then newest-created.
    return filtered.sort((a, b) => {
      if (a.orphaned !== b.orphaned) return a.orphaned ? -1 : 1;
      const aIn = IN_FLIGHT.has(a.status);
      const bIn = IN_FLIGHT.has(b.status);
      if (aIn !== bIn) return aIn ? -1 : 1;
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    });
  }, [runs, search, orphanOnly]);

  if (loading && !runs) return <Progress />;

  return (
    <>
      <HowItWorks
        lead={
          <>
            Every Backstage template run is a scaffolder <em>task</em>. This tab
            shows the in-flight ones and lets you act on a stuck run without
            touching the database. A task is <b>orphaned</b> when it is still{' '}
            <code>processing</code> but its worker has stopped heartbeating (e.g.
            the pod that was running it was replaced) — it will never finish or
            clean up on its own.
          </>
        }
        steps={HOW_IT_WORKS_STEPS}
      />

    <InfoCard
      title="Active Runs"
      subheader={
        meta
          ? `Scaffolder tasks · orphan threshold ${meta.orphanAfterSeconds}s · updated ${relTime(
              new Date(meta.builtAt).toISOString(),
            )}${meta.forceCloseEnabled ? '' : ' · force-close unavailable (non-Postgres backend)'}`
          : undefined
      }
      action={
        <IconButton
          onClick={() => load(statusFilter)}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh runs"
        >
          {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      }
    >
      {error && (
        <Box mb={2}>
          <WarningPanel title="Couldn't load runs" message={error} />
        </Box>
      )}

      <Box className={classes.toolbar}>
        <TextField
          className={classes.search}
          placeholder="Search id / template / user"
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search runs' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl className={classes.statusFilter}>
          <Select
            multiple
            displayEmpty
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as string[])}
            inputProps={{ 'aria-label': 'Filter by status' }}
            renderValue={sel =>
              (sel as string[]).length
                ? `Status: ${(sel as string[]).length}`
                : 'In-flight (default)'
            }
            // MUI v4: getContentAnchorEl:null drops the menu below the control
            // (else it anchors to the selection and pops over the header);
            // disableScrollLock avoids the page shift. Fixed-width menu matches
            // the control so it renders as a clean multi-select (checkbox rows).
            MenuProps={{
              getContentAnchorEl: null,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              disableScrollLock: true,
              PaperProps: { style: { width: 220, maxHeight: 320 } },
            }}
          >
            {STATUS_OPTIONS.map(s => (
              <MenuItem key={s} value={s} dense>
                <Checkbox
                  size="small"
                  color="primary"
                  checked={statusFilter.includes(s)}
                />
                <ListItemText primary={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={orphanOnly}
              onChange={e => setOrphanOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Orphaned only"
        />
        <span className={classes.grow} />
        <span className={classes.meta}>
          {visible.length} shown / {runs?.length ?? 0} total
        </span>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Task</TableCell>
            <TableCell>Template</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created by</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last heartbeat</TableCell>
            {canWrite && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canWrite ? 7 : 6}>
                <Box py={3} textAlign="center" className={classes.meta}>
                  No runs match the current filters.
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            visible.map(run => {
              const canCancel = IN_FLIGHT.has(run.status);
              const canRetry = run.status === 'failed' || run.status === 'cancelled';
              return (
                <TableRow key={run.id} hover>
                  <TableCell className={classes.idCell}>
                    <Tooltip title={run.id}>
                      <span>{shortId(run.id)}</span>
                    </Tooltip>
                    <IconButton size="small" onClick={() => openDetail(run)} title="Details">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                  <TableCell>{run.templateRef ?? '—'}</TableCell>
                  <TableCell>
                    <StatusMark status={run.status} orphaned={run.orphaned} />
                  </TableCell>
                  <TableCell>{run.createdBy ?? '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={run.createdAt ?? ''}>
                      <span>{relTime(run.createdAt)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={run.lastHeartbeatAt ?? 'never'}>
                      <span>{relTime(run.lastHeartbeatAt)}</span>
                    </Tooltip>
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Box className={classes.actions}>
                        {run.busy ? (
                          <CircularProgress size={18} />
                        ) : (
                          <>
                            {canCancel && (
                              <Tooltip title="Graceful cancel (signals the running worker)">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => runAction(run, 'cancel')}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canRetry && (
                              <Tooltip title="Retry">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => runAction(run, 'retry')}
                                  >
                                    <ReplayIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {run.orphaned && (
                              <Tooltip
                                title={
                                  meta?.forceCloseEnabled
                                    ? 'Force-close orphaned task (break-glass)'
                                    : 'Force-close unavailable (non-Postgres backend)'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    disabled={!meta?.forceCloseEnabled}
                                    onClick={() => setConfirmForce(run)}
                                  >
                                    <WarningIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <Dialog open={Boolean(confirmForce)} onClose={() => setConfirmForce(undefined)}>
        <DialogTitle>Force-close {confirmForce && shortId(confirmForce.id)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This directly marks the task <code>cancelled</code> in the scaffolder
            database. Use it only for a task whose worker has died (stuck{' '}
            <code>processing</code> with a stale heartbeat) — a graceful cancel
            can't reach it. The write refuses unless the task is still an orphan,
            so a task that recovers on its own is never affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmForce(undefined)}>Cancel</Button>
          <Button
            color="secondary"
            onClick={() => {
              const r = confirmForce!;
              setConfirmForce(undefined);
              runAction(r, 'force-close');
            }}
          >
            Force-close
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={Boolean(detail)}
        onClose={() => setDetail(undefined)}
        classes={{ paper: classes.drawerPaper }}
      >
        {detail && (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" className={classes.idCell}>
                {detail.id}
              </Typography>
              <IconButton size="small" onClick={() => setDetail(undefined)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box mt={1} mb={2}>
              <Chip size="small" label={detail.status} />
              {detail.orphaned && (
                <Chip
                  size="small"
                  color="secondary"
                  label="orphaned"
                  className={classes.orphanChip}
                />
              )}
              <Typography variant="body2" className={classes.meta}>
                {detail.templateRef ?? '—'} · by {detail.createdBy ?? '—'}
              </Typography>
            </Box>
            <Typography variant="subtitle2">Events</Typography>
            {detailError && <WarningPanel title="Couldn't load events" message={detailError} />}
            {!detailError && !detailEvents && <CircularProgress size={20} />}
            {detailEvents?.length === 0 && (
              <Typography variant="body2" className={classes.meta}>
                No events.
              </Typography>
            )}
            {detailEvents?.map(ev => (
              <Box key={String(ev.id)} className={classes.eventRow}>
                <span className={classes.meta}>{relTime(ev.createdAt ?? null)} </span>
                <b>{ev.type ?? ev.body?.status ?? 'event'}</b>
                {ev.body?.stepId ? ` [${ev.body.stepId}]` : ''}{' '}
                {ev.body?.message ?? ''}
              </Box>
            ))}
          </>
        )}
      </Drawer>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(undefined)}
        message={toast}
      />
    </InfoCard>
    </>
  );
};
